import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useCtaColorVars } from '../hooks/useCtaColorVars';

// useLayoutEffect warns during SSR; fall back to useEffect on the server.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export function PropertyOverview() {
  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const ctaColorVars = useCtaColorVars();
  const [expanded, setExpanded] = useState(false);
  const descRef = useRef<HTMLDivElement>(null);
  const [fullHeight, setFullHeight] = useState(0);

  // Measure the full rendered height so we can clamp the collapsed view to
  // half of it. Re-measure on resize since reflow changes line wrapping.
  useIsomorphicLayoutEffect(() => {
    const el = descRef.current;
    if (!el) return;
    const measure = () => setFullHeight(el.scrollHeight);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Content taller than a couple of lines is worth collapsing.
  const isCollapsible = fullHeight > 96;
  const collapsedHeight = Math.round(fullHeight / 2);

  return (
    <section className="pt-4 md:pt-6 pb-2 md:pb-3 bg-[var(--vibe-color-background)]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div ref={headingRef} className="reveal-fade-up">
          <RichTextContent
            path="propertyoverview.pre_title"
            as="p"
            className="overview-heading-font text-sm sm:text-base text-[var(--vibe-color-text)] mb-8 tracking-wide uppercase"
          />

          <div
            ref={descRef}
            className={`overview-description-collapse ${
              !expanded && isCollapsible ? 'is-collapsed' : ''
            }`}
            style={
              !expanded && isCollapsible
                ? { maxHeight: `${collapsedHeight}px` }
                : undefined
            }
          >
            <RichTextContent
              path="propertyoverview.description"
              as="div"
              className="overview-description-nav-font text-sm sm:text-base text-[var(--vibe-color-text)] max-w-3xl mx-auto leading-relaxed [&_a]:text-primary [&_a]:underline"
            />
          </div>

          {isCollapsible && (
            <button
              type="button"
              onClick={() => setExpanded((prev) => !prev)}
              aria-expanded={expanded}
              style={ctaColorVars}
              className="overview-description-nav-font mt-4 text-sm sm:text-base text-[var(--cta-bg,var(--vibe-color-primary))] underline underline-offset-4 hover:opacity-80 transition-opacity cursor-pointer"
            >
              {expanded ? 'Read Less' : 'Read More'}
            </button>
          )}
        </div>
      </div>

      <style>{`
        .overview-title-gradient {
          background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        /* Section title face — same Oswald stack as FloorplansList /
           ConvenienceCallout / Testimonials / LetsConnect headings. */
        .overview-heading-font,
        .overview-heading-font * {
          font-family: var(--vibe-font-heading, 'Oswald', 'Arial Narrow', system-ui, sans-serif);
          font-weight: 400;
        }
        /* Body copy stays on Space Mono (loaded globally by the Header). */
        .overview-description-nav-font,
        .overview-description-nav-font *,
        .overview-nav-font,
        .overview-nav-font * {
          font-family: var(--vibe-font-body);
        }
        /* Collapsed state: clamp the body copy to half its full height with a
           soft fade at the cut-off, then reveal the rest on Read More. */
        .overview-description-collapse {
          overflow: hidden;
        }
        .overview-description-collapse.is-collapsed {
          position: relative;
          -webkit-mask-image: linear-gradient(to bottom, black 55%, transparent 100%);
          mask-image: linear-gradient(to bottom, black 55%, transparent 100%);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.pre_title': {
      label: 'Subtitle',
      type: 'richtext',
      defaultField: 'property_home_romance_title',
      defaultValue: 'Life at our community is like nowhere else',
    },
    'propertyoverview.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_long_description',
      defaultValue: '<p>Discover the perfect blend of comfort, style, and convenience at our community.</p>',
    },
  },
};

(PropertyOverview as any).contentSchema = contentSchema;

export default PropertyOverview;
